import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft } from 'lucide-react';

export default function EditPaper() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [paper, setPaper] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    pillar: '',
    theme: '',
    sub_themes: '',
    authors: '',
    publication_date: '',
    price: '',
    keywords: '',
    status: 'active'
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const pillars = ['Economic Stability', 'Social Equity', 'Environmental Resilience'];

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/login');
      return;
    }
    fetchPaper();
  }, [user, id, navigate]);

  const fetchPaper = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/papers/${id}`
      );
      setPaper(data);
      
      // Populate form
      setFormData({
        title: data.title || '',
        description: data.description || '',
        pillar: data.pillar || '',
        theme: data.theme || '',
        sub_themes: data.sub_themes?.join(', ') || '',
        authors: data.authors?.join(', ') || '',
        publication_date: data.publication_date ? data.publication_date.split('T')[0] : '',
        price: data.price?.toString() || '',
        keywords: data.keywords?.join(', ') || '',
        status: data.status || 'active'
      });
    } catch (error) {
      console.error('Error fetching paper:', error);
      alert('Failed to load paper');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        pillar: formData.pillar,
        theme: formData.theme,
        sub_themes: formData.sub_themes.split(',').map(s => s.trim()).filter(Boolean),
        authors: formData.authors.split(',').map(s => s.trim()).filter(Boolean),
        keywords: formData.keywords.split(',').map(s => s.trim()).filter(Boolean),
        price: parseFloat(formData.price) || 0,
        publication_date: formData.publication_date ? new Date(formData.publication_date).toISOString() : null,
        status: formData.status
      };

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/papers/${id}`,
        payload,
        { withCredentials: true }
      );

      alert('Paper updated successfully!');
      navigate('/admin');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update paper');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <p className="text-gray-500">Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/admin')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card className="p-8">
          <h1 className="text-3xl font-bold text-[#1E3A5F] mb-6">Edit Research Paper</h1>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Title */}
              <div className="md:col-span-2">
                <Label htmlFor="title">Paper Title *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <Label htmlFor="description">Description *</Label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E87722] focus:border-transparent"
                />
              </div>

              {/* Pillar */}
              <div>
                <Label htmlFor="pillar">Pillar *</Label>
                <select
                  id="pillar"
                  name="pillar"
                  value={formData.pillar}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E87722] focus:border-transparent"
                >
                  <option value="">Select pillar</option>
                  {pillars.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              {/* Theme */}
              <div>
                <Label htmlFor="theme">Theme *</Label>
                <Input
                  id="theme"
                  name="theme"
                  value={formData.theme}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Sub-themes */}
              <div>
                <Label htmlFor="sub_themes">Sub-themes (comma-separated)</Label>
                <Input
                  id="sub_themes"
                  name="sub_themes"
                  value={formData.sub_themes}
                  onChange={handleChange}
                />
              </div>

              {/* Authors */}
              <div>
                <Label htmlFor="authors">Authors (comma-separated) *</Label>
                <Input
                  id="authors"
                  name="authors"
                  value={formData.authors}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Publication Date */}
              <div>
                <Label htmlFor="publication_date">Publication Date</Label>
                <Input
                  id="publication_date"
                  name="publication_date"
                  type="date"
                  value={formData.publication_date}
                  onChange={handleChange}
                />
              </div>

              {/* Keywords */}
              <div className="md:col-span-2">
                <Label htmlFor="keywords">Keywords (comma-separated)</Label>
                <Input
                  id="keywords"
                  name="keywords"
                  value={formData.keywords}
                  onChange={handleChange}
                />
              </div>

              {/* Status */}
              <div>
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E87722] focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-4">
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#E87722] hover:bg-[#d66a1a] text-white"
              >
                {submitting ? 'Updating...' : 'Update Paper'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin')}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>

      <Footer />
    </div>
  );
}
